import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, XCircle, Search, ShieldCheck, Building2, User, Clock, Sparkles } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = { pending: "Pendiente", approved: "Aprobado", rejected: "Rechazado" };
const STATUS_STYLE = {
  pending: "bg-warning/20 text-foreground",
  approved: "bg-primary/15 text-primary",
  rejected: "bg-destructive/15 text-destructive",
};

const INST_LABEL = {
  universidad: "Universidad",
  hospital: "Hospital",
  clinica: "Clínica",
  entidad_social: "Entidad social",
  investigacion: "Investigación",
  particular: "Particular",
  otro: "Otro",
};

export default function AdminUsers() {
  const [tab, setTab] = useState("pending");
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [professionals, setProfessionals] = useState([]);

  const load = useCallback(async () => {
    const [{ data: list }, { data: c }, { data: pros }] = await Promise.all([
      api.get(`/admin/users`, { params: { status: tab === "all" ? undefined : tab, q: q || undefined } }),
      api.get(`/admin/users/pending/count`),
      api.get(`/admin/professionals`),
    ]);
    setUsers(list);
    setPendingCount(c.pending);
    setProfessionals(pros);
  }, [tab, q]);

  useEffect(() => { load(); }, [load]);

  const assignPro = async (u, professional_id) => {
    try {
      const body = { professional_id: professional_id === "__none__" ? null : professional_id };
      await api.post(`/admin/users/${u.id}/assign-professional`, body);
      toast.success(professional_id === "__none__" ? "Paciente desasignado" : "Paciente asignado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const approve = async (u) => {
    try {
      await api.post(`/admin/users/${u.id}/approve`);
      toast.success(`${u.name} aprobado`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const openReject = (u) => { setRejectTarget(u); setReason(""); };

  const confirmReject = async () => {
    try {
      await api.post(`/admin/users/${rejectTarget.id}/reject`, { reason });
      toast.success(`${rejectTarget.name} rechazado`);
      setRejectTarget(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const changeRole = async (u, role) => {
    try {
      await api.post(`/admin/users/${u.id}/set-role`, { role });
      toast.success("Rol actualizado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const [prewarming, setPrewarming] = useState(false);
  const prewarm = async () => {
    setPrewarming(true);
    try {
      const { data } = await api.post("/ai/prewarm-media", { sessions: [4, 5, 8, 9], kinds: ["image", "audio"] });
      toast.success(`Media generada: ${data.images} imágenes, ${data.audios} audios${data.failed ? ` · ${data.failed} fallos` : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setPrewarming(false); }
  };

  return (
    <div className="space-y-8" data-testid="admin-users-page">
      <div>
        <p className="text-sm uppercase tracking-widest text-primary font-semibold">Administración</p>
        <h1 className="heading text-4xl font-extrabold mt-1">Autorización de cuentas</h1>
        <p className="text-lg text-foreground/70 mt-2">Aprueba o rechaza el acceso a la plataforma. Verifica la entidad y el rol solicitado.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="rounded-2xl border-border">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-warning/20 text-foreground grid place-items-center">
              <Clock size={22} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Pendientes</div>
              <div className="heading text-3xl font-extrabold">{pendingCount}</div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-border">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/15 text-primary grid place-items-center">
              <ShieldCheck size={22} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Total gestionadas</div>
              <div className="heading text-3xl font-extrabold">{users.length}</div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-accent/30 bg-accent/5">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={16} className="text-accent" />
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Contenido multimedia</div>
            </div>
            <p className="text-sm text-foreground/70 mb-3">Genera imágenes y audios para las sesiones 4, 5, 8 y 9.</p>
            <Button onClick={prewarm} disabled={prewarming} className="rounded-full w-full h-10" data-testid="prewarm-media-btn">
              {prewarming ? "Generando..." : "Generar ahora"}
            </Button>
          </CardContent>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="h-auto rounded-full p-1 bg-secondary">
          <TabsTrigger value="pending" className="rounded-full px-4 h-9" data-testid="tab-pending">
            Pendientes {pendingCount > 0 && <Badge className="ml-2 bg-warning text-foreground">{pendingCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="approved" className="rounded-full px-4 h-9" data-testid="tab-approved">Aprobadas</TabsTrigger>
          <TabsTrigger value="rejected" className="rounded-full px-4 h-9" data-testid="tab-rejected">Rechazadas</TabsTrigger>
          <TabsTrigger value="all" className="rounded-full px-4 h-9" data-testid="tab-all">Todas</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-6 space-y-3">
          {users.length === 0 && (
            <p className="text-muted-foreground py-8 text-center border border-dashed border-border rounded-xl">
              No hay solicitudes en esta categoría.
            </p>
          )}
          {users.map((u) => (
            <Card key={u.id} className="rounded-2xl border-border" data-testid={`admin-user-${u.id}`}>
              <CardContent className="p-5">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-64">
                    <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center">
                      <User size={20} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="heading font-bold text-lg">{u.name}</h3>
                        <Badge className={STATUS_STYLE[u.approval_status] || ""}>{STATUS_LABEL[u.approval_status] || u.approval_status}</Badge>
                        <Badge variant="outline" className="capitalize">{u.role}</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">{u.email}{u.country ? ` · ${u.country}` : ""}</p>
                      {u.institution && (
                        <p className="text-sm mt-1 flex items-center gap-1">
                          <Building2 size={13} className="text-muted-foreground" />
                          <span className="font-medium">{u.institution}</span>
                          <span className="text-muted-foreground">({INST_LABEL[u.institution_type] || u.institution_type})</span>
                        </p>
                      )}
                      {u.purpose && (
                        <p className="text-sm text-foreground/80 mt-2 italic border-l-2 border-border pl-2">"{u.purpose}"</p>
                      )}
                      {u.rejection_reason && (
                        <p className="text-xs text-destructive mt-2">Motivo rechazo: {u.rejection_reason}</p>
                      )}
                      {u.role === "patient" && u.professional_id && (
                        <p className="text-xs mt-1 flex items-center gap-1 text-primary">
                          <User size={12} />
                          <span>Profesional: {professionals.find((p) => p.id === u.professional_id)?.name || u.professional_id}</span>
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">Solicitado: {new Date(u.created_at).toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 items-stretch min-w-52">
                    <Select value={u.role} onValueChange={(v) => changeRole(u, v)}>
                      <SelectTrigger className="h-9" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="patient">Paciente</SelectItem>
                        <SelectItem value="professional">Profesional</SelectItem>
                        <SelectItem value="researcher">Investigador</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>

                    {u.role === "patient" && (
                      <Select
                        value={u.professional_id || "__none__"}
                        onValueChange={(v) => assignPro(u, v)}
                      >
                        <SelectTrigger className="h-9" data-testid={`pro-select-${u.id}`}>
                          <SelectValue placeholder="Sin profesional" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Sin profesional asignado</SelectItem>
                          {professionals.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name} · {p.email} ({p.patients_count})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    <div className="flex gap-2">
                      {u.approval_status !== "approved" && (
                        <Button size="sm" onClick={() => approve(u)} className="rounded-full flex-1" data-testid={`approve-btn-${u.id}`}>
                          <CheckCircle2 size={14} className="mr-1" /> Aprobar
                        </Button>
                      )}
                      {u.approval_status !== "rejected" && (
                        <Button size="sm" variant="outline" onClick={() => openReject(u)}
                          className="rounded-full flex-1 border-destructive/40 text-destructive hover:bg-destructive/10"
                          data-testid={`reject-btn-${u.id}`}>
                          <XCircle size={14} className="mr-1" /> Rechazar
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      <Dialog open={!!rejectTarget} onOpenChange={(v) => !v && setRejectTarget(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Rechazar solicitud</DialogTitle>
            <DialogDescription>
              {rejectTarget && `${rejectTarget.name} · ${rejectTarget.email}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Motivo (opcional, visible internamente)</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
              placeholder="Ej: entidad no verificable, rol no adecuado..." data-testid="reject-reason-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectTarget(null)} className="rounded-full">Cancelar</Button>
            <Button onClick={confirmReject} className="rounded-full" data-testid="confirm-reject-btn">
              Rechazar solicitud
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
